import { z } from 'zod/v4'

import {
  BRIDGE_CONTRACT_DIRECTIONS,
  METHOD_SIGNATURE_CATEGORIES,
} from '../../types/blockchain-classification-catalogs.types.js'
import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

/*
 * «Clasificación blockchain»: coverage, inbox, issues and the three classifier catalogs. Each schema reflects an
 * interface of `types/blockchain-classification*.types.ts` with the same names and optionality, every object strict.
 * `shared-blockchain-classification-screen.type-assertions.ts` ties them in both directions. The attribute map is not
 * here: it stays on its own route.
 */

export const classificationCoverageNetworkSchema = z.strictObject({
  chainId: z.number(),
  slug: z.string(),
  displayName: z.string(),
  /** Open: `'evm' | 'svm' | 'utxo' | 'unmapped' | (string & {})`. */
  vmFamily: z.string(),
  brandColor: z.string().nullable(),
  availability: z.enum(['operational', 'disabled', 'no_enabled_rpc', 'no_adapter', 'internal']),
  enabledRpcEndpointCount: z.number(),
  transactionCount: z.number(),
  exactCount: z.number(),
  heuristicCount: z.number(),
  unknownCount: z.number(),
  inboxGroupCount: z.number(),
})

export const classificationCoverageSchema = z.strictObject({
  windowDays: z.number(),
  generatedAt: z.string(),
  totals: z.strictObject({
    transactionCount: z.number(),
    exactCount: z.number(),
    heuristicCount: z.number(),
    unknownCount: z.number(),
    inboxGroupCount: z.number(),
    inboxTransactionCount: z.number(),
  }),
  networks: z.array(classificationCoverageNetworkSchema),
})

export const classificationInboxGroupSchema = z.strictObject({
  id: z.string(),
  chainId: z.number(),
  networkSlug: z.string(),
  contractAddress: z.string().nullable(),
  methodId: z.string().nullable(),
  methodName: z.string().nullable(),
  transactionCount: z.number(),
  tenantCount: z.number(),
  walletCount: z.number(),
  revertedCount: z.number(),
  lastSeenAt: z.string(),
  dominantClassification: z.string(),
  dominantAttribute: z.string().nullable(),
  dominantShare: z.number(),
  averageConfidence: z.number(),
  sampleTxHashes: z.array(z.string()),
})

export const classificationInboxPageSchema = z.strictObject({
  windowDays: z.number(),
  chainId: z.number().nullable(),
  totalGroups: z.number(),
  totalTransactions: z.number(),
  groups: z.array(classificationInboxGroupSchema),
})

export const classificationIssueSchema = z.strictObject({
  id: z.string(),
  code: z.enum([
    'map_attribute_requires_missing_leg',
    'map_income_attribute_on_outgoing_flow',
    'map_fiscal_implication_contradicts_attribute',
    'template_rules_cannot_fire',
    'global_rules_inert_on_evm',
    'template_match_marked_requires_review',
    'high_impact_inbox_group',
    'svm_raw_payload_purged',
  ]),
  severity: z.enum(['fiscal', 'pending', 'info']),
  facts: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
  affectedTransactionCount: z.number().nullable(),
})

export const classificationIssuesSchema = z.strictObject({
  windowDays: z.number(),
  generatedAt: z.string(),
  issues: z.array(classificationIssueSchema),
})

export const classificationMethodSignatureSchema = z.strictObject({
  id: z.string(),
  selector: z.string(),
  name: z.string(),
  classification: z.string(),
  protocol: z.string().nullable(),
  category: z.enum(METHOD_SIGNATURE_CATEGORIES),
  enabled: z.boolean(),
  updatedAt: z.string(),
})

export const classificationBridgeContractSchema = z.strictObject({
  id: z.string(),
  networkSlug: z.string(),
  contractAddress: z.string(),
  name: z.string(),
  direction: z.enum(BRIDGE_CONTRACT_DIRECTIONS),
  destinationChain: z.number().nullable(),
  enabled: z.boolean(),
  updatedAt: z.string(),
})

export const classificationNftMarketplaceSchema = z.strictObject({
  id: z.string(),
  networkSlug: z.string(),
  contractAddress: z.string(),
  name: z.string(),
  marketplaceFamily: z.string().nullable(),
  enabled: z.boolean(),
  updatedAt: z.string(),
})

/**
 * The limits of the old routes (`classification.validators.ts:12,28-29`). No `offset`: the page always asks from 0 and
 * «Cargar más» raises `limit`.
 */
export const blockchainClassificationQuerySchema = z.strictObject({
  windowDays: z.coerce.number<number>().int().min(1).max(365).default(30),
  chainId: z.coerce.number<number>().int().positive().optional(),
  limit: z.coerce.number<number>().int().min(1).max(200).default(50),
})

export const adminBlockchainClassificationScreen = defineScreen({
  id: 'admin.blockchainClassification',
  path: '/api/screens/admin/blockchain-classification',
  access: 'super_admin',
  query: blockchainClassificationQuerySchema,
  response: z.strictObject({
    coverage: screenSection(classificationCoverageSchema),
    inbox: screenSection(classificationInboxPageSchema),
    issues: screenSection(classificationIssuesSchema),
    methodSignatures: screenSection(z.array(classificationMethodSignatureSchema)),
    bridgeContracts: screenSection(z.array(classificationBridgeContractSchema)),
    nftMarketplaces: screenSection(z.array(classificationNftMarketplaceSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.blockchainClassification'],
  budget: { requests: 1, statements: 40, bytes: 200_000, p95Ms: 1500 },
  summary:
    'Blockchain classification workspace: coverage per network, pending inbox, detected issues and the three classifier catalogs',
})

export type AdminBlockchainClassificationQuery = ScreenQuery<
  typeof adminBlockchainClassificationScreen
>
export type AdminBlockchainClassificationData = ScreenData<
  typeof adminBlockchainClassificationScreen
>
